// Splits a parsed cell into renderable segments by walking `cell.text`
// and slotting an inline chip at each source anchor's rendered token
// (`linkLabel`), in document order. A cell whose only non-chip text is
// separators (commas / semicolons / whitespace) between citations is a
// "pure source list" — every token is a page citation, so its chips
// render as `p. {page}` rather than echoing the raw link label.
import type { CellSourceRef, CellSpec } from "./parse-markdown-table";

export type CellSegment =
    | { kind: "text"; text: string }
    | { kind: "chip"; source: CellSourceRef };

export interface SegmentedSourceCell {
    segments: CellSegment[];
    isPureSourceList: boolean;
}

// A gap between (or around) citations that contains only these chars
// keeps a cell in the pure-source-list bucket; any other character is
// prose, which routes the cell to mixed-content rendering instead.
const SEPARATORS_ONLY = /^[\s,;]*$/;

export function segmentSourceCell(cell: CellSpec): SegmentedSourceCell {
    const sources = cell.sources ?? [];
    const { text } = cell;

    const segments: CellSegment[] = [];
    let cursor = 0;
    let chipCount = 0;
    let placedEverySource = sources.length > 0;
    let everyGapIsSeparator = true;

    const pushText = (value: string) => {
        if (!value) return;
        segments.push({ kind: "text", text: value });
        if (!SEPARATORS_ONLY.test(value)) everyGapIsSeparator = false;
    };

    for (const source of sources) {
        const label = source.linkLabel;
        // A source whose rendered token is absent (structural link with no
        // text) or isn't found from the cursor (a label consumed by an
        // earlier chip) can't be placed: it leaves the cell impure and
        // renders no chip.
        if (!label) {
            placedEverySource = false;
            continue;
        }
        const at = text.indexOf(label, cursor);
        if (at === -1) {
            placedEverySource = false;
            continue;
        }
        pushText(text.slice(cursor, at));
        segments.push({ kind: "chip", source });
        chipCount += 1;
        cursor = at + label.length;
    }
    pushText(text.slice(cursor));

    // Guarantee at least one segment so callers can always render `text`.
    if (segments.length === 0) segments.push({ kind: "text", text });

    return {
        segments,
        isPureSourceList:
            chipCount > 0 && placedEverySource && everyGapIsSeparator,
    };
}

export function isPureSourceListCell(cell: CellSpec): boolean {
    return segmentSourceCell(cell).isPureSourceList;
}
